import { NavLink, useNavigate } from "react-router-dom";
import { Home, FolderOpen, Users2, FileSignature, FileSpreadsheet, LineChart, Activity, Settings, LogOut, Menu, X, Plus, FolderPlus, Upload, FileSignature as ContractIcon, CalendarClock, Music, Percent } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useDriveAuth } from "../context/DriveAuthContext";
import { LOGO_URL } from "../../lib/data";

const NAV_ALL = [
  { to: "/drive/hub", icon: Home, label: "Start", roles: ["superadmin", "staff", "artist", "customer"] },
  { to: "/drive/files", icon: FolderOpen, label: "Dateien", roles: ["artist", "customer"] },
  { to: "/drive/crm", icon: Users2, label: "CRM", roles: ["superadmin", "staff"] },
  { to: "/drive/contracts", icon: FileSignature, label: "Verträge", roles: ["superadmin", "staff"] },
  { to: "/drive/invoices", icon: FileSpreadsheet, label: "Rechnungen", roles: ["superadmin", "staff"] },
  { to: "/drive/musichub", icon: Music, label: "Music Hub", roles: ["superadmin", "staff"] },
  { to: "/drive/provisions", icon: Percent, label: "Provisionen", roles: ["superadmin", "staff"] },
  { to: "/drive/monatsabschluss", icon: CalendarClock, label: "Monatsabschluss", roles: ["superadmin", "staff"] },
  { to: "/drive/dashboard", icon: LineChart, label: "Übersicht", roles: ["superadmin", "staff"] },
  { to: "/drive/activity", icon: Activity, label: "Aktivität", roles: ["superadmin"] },
  { to: "/drive/settings", icon: Settings, label: "Benutzer", roles: ["superadmin"] },
];

export default function DriveLayout({ children }) {
  const { profile, signOut, role } = useDriveAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const addRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (addRef.current && !addRef.current.contains(e.target)) setAddOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const items = NAV_ALL.filter((n) => (role ? n.roles.includes(role) : n.label === "Start"));

  const dbMissing = profile?._dbMissing;

  const roleBadge = {
    superadmin: { label: "SUPERADMIN", cls: "bg-[#ff0033]/15 text-[#ff0033] border-[#ff0033]/40" },
    staff: { label: "MITARBEITER", cls: "bg-white/10 text-white border-white/20" },
    artist: { label: "KÜNSTLER", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" },
    customer: { label: "KUNDE", cls: "bg-blue-500/15 text-blue-400 border-blue-500/40" },
  }[role] || { label: "GAST", cls: "bg-zinc-800 text-zinc-400 border-zinc-700" };

  const handleSignOut = async () => {
    await signOut();
    nav("/drive/login");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-[#0A0A0A] border-r border-white/[0.06] flex flex-col z-50 transition-transform lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo */}
        <div className="px-6 py-6 border-b border-white/[0.06]">
          <a href="/" className="flex items-center gap-3 group">
            <img src={LOGO_URL} alt="Straßenwert" className="h-8 w-auto drop-shadow-[0_0_10px_rgba(255,0,51,0.4)]" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold group-hover:text-white transition-colors">
              Drive
            </span>
          </a>
        </div>

        {/* + Hinzufügen (only for staff/superadmin) */}
        {(role === "superadmin" || role === "staff") && (
          <div ref={addRef} className="relative px-4 pt-4">
            <button
              onClick={() => { setAddOpen((v) => !v); }}
              data-testid="sidebar-add-btn"
              className="w-full group inline-flex items-center justify-between gap-2 bg-white text-black px-4 py-3 text-[10px] font-bold tracking-[0.25em] uppercase hover:bg-[#ff0033] hover:text-white transition-colors"
            >
              <span className="flex items-center gap-2"><Plus size={14} /> Hinzufügen</span>
              <span className={`transition-transform ${addOpen ? "rotate-45" : ""}`}>+</span>
            </button>
            {addOpen && (
              <div className="absolute left-4 right-4 mt-1 bg-[#0F0F0F] border border-white/10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)] z-50 divide-y divide-white/[0.05]">
                <button
                  onClick={() => { setAddOpen(false); setOpen(false); nav("/drive/files?action=new-folder"); }}
                  data-testid="add-folder"
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white hover:bg-[#ff0033]/10 transition-colors text-left"
                >
                  <FolderPlus size={15} className="text-[#ff0033]" /> Ordner erstellen
                </button>
                <button
                  onClick={() => { setAddOpen(false); setOpen(false); nav("/drive/files?action=upload"); }}
                  data-testid="add-upload"
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white hover:bg-[#ff0033]/10 transition-colors text-left"
                >
                  <Upload size={15} className="text-[#ff0033]" /> Datei hochladen
                </button>
                <button
                  onClick={() => { setAddOpen(false); setOpen(false); nav("/drive/contracts?action=new"); }}
                  data-testid="add-contract"
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white hover:bg-[#ff0033]/10 transition-colors text-left"
                >
                  <ContractIcon size={15} className="text-[#ff0033]" /> Neuen Vertrag
                </button>
                <button
                  onClick={() => { setAddOpen(false); setOpen(false); nav("/drive/crm?action=new-artist"); }}
                  data-testid="add-artist"
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white hover:bg-[#ff0033]/10 transition-colors text-left"
                >
                  <Users2 size={15} className="text-[#ff0033]" /> Neuer Künstler / Kunde
                </button>
              </div>
            )}
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm font-medium tracking-wide transition-all rounded-sm ${
                    isActive
                      ? "bg-[#ff0033]/10 text-white border-l-2 border-[#ff0033] shadow-[0_0_20px_rgba(255,0,51,0.15)]"
                      : "text-zinc-400 hover:text-white hover:bg-white/[0.03] border-l-2 border-transparent"
                  }`
                }
              >
                <Icon size={16} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-white/[0.06]">
          <div className="mb-3">
            <div className="text-xs text-zinc-300 truncate">{profile?.display_name || "Ohne Namen"}</div>
            <span className={`mt-2 inline-block text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-1 border ${roleBadge.cls}`}>
              {roleBadge.label}
            </span>
          </div>
          <button
            onClick={handleSignOut}
            data-testid="drive-signout"
            className="w-full flex items-center gap-2 px-3 py-2 text-xs tracking-wide text-zinc-400 hover:text-white border border-white/10 hover:border-[#ff0033]/40 transition-colors"
          >
            <LogOut size={12} /> Ausloggen
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && <div onClick={() => setOpen(false)} className="fixed inset-0 bg-black/60 z-40 lg:hidden" />}

      {/* Content */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Top bar (mobile toggle) */}
        <div className="lg:hidden sticky top-0 z-30 bg-[#050505]/90 backdrop-blur-xl border-b border-white/[0.06] px-4 py-3 flex items-center justify-between">
          <button data-testid="drive-menu-toggle" onClick={() => setOpen(!open)} className="p-2 text-zinc-300">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">Drive</div>
          <span className={`text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-1 border ${roleBadge.cls}`}>{roleBadge.label}</span>
        </div>

        <div className="flex-1 p-6 md:p-10 max-w-7xl mx-auto w-full">
          {dbMissing && (
            <div className="mb-8 border border-[#ff0033]/40 bg-[#ff0033]/[0.06] p-5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-2">
                ⚠ Datenbank nicht initialisiert
              </div>
              <div className="text-sm text-zinc-200 leading-relaxed">
                Die Drive-Tabellen existieren noch nicht in deinem Supabase-Projekt. Bitte führe <strong className="text-white font-mono">DRIVE_MIGRATION.sql</strong> im Supabase SQL Editor aus.
                Danach <strong className="text-white font-mono">DRIVE_HOTFIX_ACCESS.sql</strong>, um dich als Superadmin zu setzen.
              </div>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
